import { useMatches } from 'react-router';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

const environmentModeSchema = z.enum(['all', 'single', 'multiple']);

const environmentModeHandleSchema = z.object({
  environmentMode: environmentModeSchema.optional(),
});

type EnvironmentMode = z.infer<typeof environmentModeSchema>;

/**
 * Returns the environment mode from the entity root route (not child routes).
 * This helps distinguish between:
 * - Environment-scoped entities (experiments, segments) that have 'single' at entity level
 * - Project-scoped entities (flags) that don't have environmentMode at entity level
 */
export function useRootEnvironmentMode(): EnvironmentMode | undefined {
  const matches = useMatches();

  // Find routes with environment modes, sorted by path depth (shortest first)
  const routesWithModes = matches
    .filter((match) => {
      const parse = environmentModeHandleSchema.safeParse(match.handle);
      return parse.success && parse.data.environmentMode !== undefined;
    })
    .sort((a, b) => a.pathname.split('/').length - b.pathname.split('/').length);

  if (routesWithModes.length === 0) {
    return undefined;
  }

  // For entity-level modes, we want routes at depth 4: /projects/:projectKey/entityType/:entityKey
  // Routes deeper than this (like /projects/:projectKey/flags/:flagKey/targeting) are tab-level
  const entityLevelRoute = routesWithModes.find((match) => {
    const segments = match.pathname.split('/').filter((s) => s.length > 0);
    return segments.length === 4;
  });

  if (entityLevelRoute) {
    const parse = environmentModeHandleSchema.safeParse(entityLevelRoute.handle);
    return parse.success ? parse.data.environmentMode : undefined;
  }

  return undefined;
}
